import { useQuery } from "@tanstack/react-query";
import { format } from "date-fns";
import { supabase } from "@/integrations/supabase/client";
import type { Restaurant } from "@/lib/supabase";
import type { MenuWithItems } from "@/hooks/useAllDailyMenus";

export const PREVIEW_TOKEN_KEY = "napihami_preview_token";

export type PreviewResult =
  | { status: "ok"; expires_at: string; restaurant: Restaurant; menus: MenuWithItems[] }
  | { status: "expired" | "invalid" };

export async function fetchRestaurantPreview(token: string, date: Date): Promise<PreviewResult> {
  const { data, error } = await supabase.rpc("get_restaurant_preview", {
    p_token: token,
    p_date: format(date, "yyyy-MM-dd"),
  });
  if (error) throw error;
  return data as unknown as PreviewResult;
}

export function getStoredPreviewToken(): string | null {
  try {
    return sessionStorage.getItem(PREVIEW_TOKEN_KEY);
  } catch {
    return null;
  }
}

export function useRestaurantPreview(token: string | null, date: Date) {
  return useQuery({
    queryKey: ["restaurant-preview", token, format(date, "yyyy-MM-dd")],
    queryFn: () => fetchRestaurantPreview(token!, date),
    enabled: Boolean(token),
    staleTime: 60_000,
  });
}

/** Human-readable remaining time, e.g. "2 nap 18 óra" or "45 perc". */
export function formatRemaining(expiresAt: string, now: Date = new Date()): string {
  const ms = new Date(expiresAt).getTime() - now.getTime();
  if (ms <= 0) return "lejárt";
  const totalMinutes = Math.floor(ms / 60000);
  const days = Math.floor(totalMinutes / 1440);
  const hours = Math.floor((totalMinutes % 1440) / 60);
  const minutes = totalMinutes % 60;
  if (days > 0) return `${days} nap ${hours} óra`;
  if (hours > 0) return `${hours} óra ${minutes} perc`;
  return `${minutes} perc`;
}
